import { Add, Balance, BarChart, CheckCircle, Contrast, FileDownload, PriorityHigh, Settings, Tune, Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { getContrastRatio } from '@mui/material/styles';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import {
  HedgePlanBudgetTriggerModel,
  HedgePlanExecutionType,
  HedgePlanRecommendationReason,
  HedgePlanSeasonSummaryModel,
  HedgePlanTermReportModel,
  HedgePlanTimeTriggerModel,
  HedgePlanTradeModel,
  HedgePlanTriggerFulfillmentStatus,
  HedgePlanValueTriggerModel,
} from '../../../../Generated/Commodity-Management-Api';
import { StoneXButton } from '../../../StoneXMui';
import { copyHedgePlanTable, downloadHedgePlanPowerPoint, HedgePlanChartSlide, HedgePlanExportBar, HedgePlanExportCell, HedgePlanExportTable } from './exportHedgePlanTable';
import { readHedgePlanColor } from './HedgePlanTriggerStyles';

const RECOMMENDATION_REASON_LABELS: Record<HedgePlanRecommendationReason, string> = {
  OverHedged: 'Over hedged',
  OverTarget: 'Over target',
  MissedTimeTrigger: 'Missed time trigger',
  ApproachingTimeTrigger: 'Approaching time trigger',
  HitValueTrigger: 'Hit value trigger',
};

// Trigger/trade data (hedge position, recommendation, target fulfillment) loads independently of
// and much faster than the price/decile matrix report - `matrix` is optional here so this table
// can render as soon as trigger/trade data is in, instead of waiting on the slower report the way
// the chart and matrix table (which need real decile values to draw) still have to.
export interface HedgePlanHedgesTableEntry {
  periodKey: string;
  periodLabel: string;
  matrix?: HedgePlanTermReportModel;
  valueTriggers: HedgePlanValueTriggerModel[];
  budgetTriggers: HedgePlanBudgetTriggerModel[];
  timeTriggers: HedgePlanTimeTriggerModel[];
  trades: HedgePlanTradeModel[];
}

interface HedgePlanHedgesTableProps {
  entries: HedgePlanHedgesTableEntry[];
  periods: Array<{ key: string; label: string }>;
  // True while the plan's trigger/trade data itself is still loading (fast). Separate from
  // `loadingPeriodKeys`, which tracks the much slower per-period price/decile matrix report.
  dataLoading: boolean;
  loadingPeriodKeys: Set<string>;
  selectedPeriodKey?: string;
  onToggleChart: (periodKey: string) => void;
  onManageSeason: (periodKey: string) => void;
  nextSeasonLabel: string;
  onAddNextSeason: () => void;
  addingNextSeason: boolean;
  onOpenSettings: () => void;
  // Title of the exported table, e.g. the plan name.
  exportTitle: string;
  // A chart and matrix slide for each season, added after the table.
  getChartSlides?: () => HedgePlanChartSlide[];
  // Budget prices only exist once a budget is linked.
  showBudgetPrice: boolean;
  weightByVolume: boolean;
  canWeightByVolume: boolean;
  onToggleWeightByVolume: () => void;
  // Shows hedge amounts as volumes in the season's unit instead of percents, where the backend has a volume.
  showVolumes: boolean;
  onToggleShowVolumes: () => void;
  // View only: just the table and the season chart buttons.
  readOnly: boolean;
}

const HEADER_COLOR = 'var(--stoneX-midnight)';
// One neutral gray across all three trigger sections. Kept as separate constants
// so a section can be re-tinted on its own without touching the others.
const VALUE_CELL_COLOR = 'var(--gray8)';
const BUDGET_CELL_COLOR = 'var(--gray8)';
const TIME_CELL_COLOR = 'var(--gray8)';
const BORDER_COLOR = 'var(--gray5)';
const PERIOD_COLUMN_MINIMUM_WIDTH = 110;
const PRICE_COLUMN_WIDTH = 90;
const VALUE_COLUMN_WIDTH = 66;
const BUDGET_COLUMN_WIDTH = 100;
const TIME_COLUMN_WIDTH = 88;
const HEDGE_POSITION_COLUMN_WIDTH = 260;
const RECOMMENDATION_COLUMN_WIDTH = 110;
const BODY_ROW_HEIGHT = 80;

function fixedColumnWidth(width: number) {
  return { width, minWidth: width, maxWidth: width };
}

function formatSeasonVolume(report: HedgePlanTermReportModel): string {
  if (report.totalVolume === null || report.totalVolume === undefined) {
    return 'Budget incomplete';
  }
  return Math.round(report.totalVolume).toLocaleString('en-US');
}

function formatPercent(percent: number): string {
  return `${Number(percent.toFixed(2))}%`;
}

// A volume when the table shows volumes and the backend returned one, otherwise the percent.
function formatHedgeAmount(percent: number | null | undefined, volume: number | null | undefined, showVolumes: boolean): string {
  if (showVolumes && volume !== null && volume !== undefined) {
    return Math.round(volume).toLocaleString('en-US');
  }
  return percent === null || percent === undefined ? '-' : formatPercent(percent);
}

function formatPrice(price: number | null | undefined): string {
  if (price === undefined || price === null) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(price);
}

function PeriodActions(props: {
  periodKey: string;
  periodLabel: string;
  chartSelected: boolean;
  chartDisabled: boolean;
  onToggleChart: (periodKey: string) => void;
  onManageSeason?: (periodKey: string) => void;
}) {
  return (
    <Box sx={{ position: 'absolute', top: 0, left: 'calc(100% + 8px)', height: '100%', display: 'flex', alignItems: 'center', gap: '2px' }}>
      <Tooltip title={props.chartSelected ? `Hide ${props.periodLabel} chart` : `Show ${props.periodLabel} chart`}>
        <span>
          <IconButton
            color={props.chartSelected ? 'primary' : 'default'}
            disabled={props.chartDisabled}
            aria-label={props.chartSelected ? `Hide ${props.periodLabel} chart` : `Show ${props.periodLabel} chart`}
            aria-pressed={props.chartSelected}
            onClick={() => props.onToggleChart(props.periodKey)}
          >
            <BarChart fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>
      {props.onManageSeason && (
        <Tooltip title={`${props.periodLabel} triggers, trades and settings`}>
          <IconButton aria-label={`${props.periodLabel} season settings`} onClick={() => props.onManageSeason?.(props.periodKey)}>
            <Tune fontSize="medium" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function TargetCell(props: {
  children: ReactNode;
  section: 'value' | 'budget' | 'time';
  status?: HedgePlanTriggerFulfillmentStatus;
  width?: number;
}) {
  const { children, section, status } = props;
  const width = props.width ?? (section === 'value' ? VALUE_COLUMN_WIDTH : section === 'budget' ? BUDGET_COLUMN_WIDTH : TIME_COLUMN_WIDTH);
  const backgroundColor = section === 'value' ? VALUE_CELL_COLOR : section === 'budget' ? BUDGET_CELL_COLOR : TIME_CELL_COLOR;
  return (
    <TableCell
      align="center"
      sx={{
        position: 'relative',
        ...fixedColumnWidth(width),
        boxSizing: 'border-box',
        height: BODY_ROW_HEIGHT,
        padding: '8px 16px 8px 8px !important',
        border: `1px solid ${BORDER_COLOR}`,
        backgroundColor,
        color: 'var(--font-color)',
        verticalAlign: 'middle',
      }}
    >
      {children}
      {status && status !== HedgePlanTriggerFulfillmentStatus.None && (
        // The two states differ in shape, a half-filled circle against a filled one with a check, so they don't rely on colour.
        <Tooltip title={status === HedgePlanTriggerFulfillmentStatus.Full ? 'Fully executed' : 'Partially executed'}>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              right: 3,
              bottom: 3,
              display: 'inline-flex',
              color: 'var(--font-color)',
            }}
          >
            {status === HedgePlanTriggerFulfillmentStatus.Full
              ? <CheckCircle sx={{ fontSize: 16 }} />
              : <Contrast sx={{ fontSize: 16 }} />}
          </Box>
        </Tooltip>
      )}
    </TableCell>
  );
}

// Positions on the bar are always percents; the labels can be volumes.
function HedgePosition(props: {
  minimumPercent: number;
  maximumPercent: number;
  currentPercent: number;
  recommendationPercent: number;
  minimumLabel: string;
  maximumLabel: string;
  currentLabel: string;
}) {
  const clampPercent = (percent: number) => Math.min(100, Math.max(0, percent));
  const minimumPosition = clampPercent(props.minimumPercent);
  const maximumPosition = clampPercent(props.maximumPercent);
  const currentPosition = clampPercent(props.currentPercent);
  // Resolved (not left as `var(--x)`) because getContrastRatio needs a literal color to compute against.
  const currentColor = props.recommendationPercent > 0
    ? readHedgePlanColor('--hedge-plan-recommended-background')
    : readHedgePlanColor('--hedge-plan-position-background');
  // '#fff'/'#000' here are the two candidate text colors being tested for contrast, not theme
  // values — the WCAG contrast formula's reference points, not something a rebrand would change.
  const currentTextColor = getContrastRatio(currentColor, '#fff') >= getContrastRatio(currentColor, '#000')
    ? 'var(--contrast-text-light)'
    : 'var(--contrast-text-dark)';
  const labelTransform = (position: number) => {
    if (position <= 12) {
      return 'translateX(0)';
    }
    if (position >= 88) {
      return 'translateX(-100%)';
    }
    return 'translateX(-50%)';
  };

  // Min/max labels can land over either the fill or the empty track, so they get a light chip
  // instead of a per-position contrast color.
  const markerLabelSx = {
    position: 'absolute' as const,
    zIndex: 1,
    padding: '2px 4px',
    borderRadius: '2px',
    backgroundColor: 'var(--white)',
    color: 'var(--font-color)',
    fontWeight: 400,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box
      aria-label={`Minimum ${props.minimumLabel}, maximum ${props.maximumLabel}, current ${props.currentLabel}`}
      sx={{
        position: 'relative',
        width: '100%',
        // The cell's own 1px top/bottom borders take the rest of the row height.
        height: BODY_ROW_HEIGHT - 2,
        overflow: 'hidden',
        backgroundColor: 'var(--gray8)',
        fontSize: '11px',
        lineHeight: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: `${currentPosition}%`,
          backgroundColor: currentColor,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${minimumPosition}%`,
          borderLeft: `2px solid ${currentPosition >= minimumPosition ? 'var(--white)' : 'var(--gray2)'}`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${maximumPosition}%`,
          borderLeft: `2px solid ${currentPosition >= maximumPosition ? 'var(--white)' : 'var(--font-color)'}`,
        }}
      />
      <Box sx={{ ...markerLabelSx, top: 6, left: `${minimumPosition}%`, transform: labelTransform(minimumPosition) }}>
        <strong>{props.minimumLabel}</strong> (Min)
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: currentPosition >= 18 ? 0 : `${currentPosition}%`,
          width: currentPosition >= 18 ? `${currentPosition}%` : 'auto',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: currentPosition >= 18 ? 'flex-end' : 'flex-start',
          paddingLeft: currentPosition >= 18 ? 0 : '6px',
          paddingRight: currentPosition >= 18 ? '8px' : 0,
          color: currentPosition >= 18 ? currentTextColor : 'var(--font-color)',
          fontSize: '13px',
          fontWeight: 700,
        }}
      >
        {props.currentLabel}
      </Box>
      <Box sx={{ ...markerLabelSx, bottom: 6, left: `${maximumPosition}%`, transform: labelTransform(maximumPosition) }}>
        <strong>{props.maximumLabel}</strong> (Max)
      </Box>
    </Box>
  );
}

function ordinal(value: number): string {
  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) {
    return `${value}th`;
  }
  return `${value}${value % 10 === 1 ? 'st' : value % 10 === 2 ? 'nd' : value % 10 === 3 ? 'rd' : 'th'}`;
}

export default function HedgePlanHedgesTable(props: HedgePlanHedgesTableProps) {
  const [showTriggerColumns, setShowTriggerColumns] = useState<boolean>(true);
  const valuePercentiles = Array.from(new Set(
    props.entries.flatMap((entry) => entry.valueTriggers
      .filter((trigger) => !trigger.disabled)
      .map((trigger) => trigger.decilePercentile)),
  )).sort((left, right) => right - left);
  const maximumTimeTargets = Math.max(
    0,
    ...props.entries.map((entry) => entry.timeTriggers.filter((trigger) => !trigger.disabled).length),
  );
  const maximumBudgetTargets = Math.max(
    0,
    ...props.entries.map((entry) => entry.budgetTriggers.filter((trigger) => !trigger.disabled).length),
  );
  const latestQuoteDate = props.entries
    .flatMap((entry) => entry.matrix?.priceSeries ?? [])
    .filter((point) => point.date)
    .map((point) => point.date!)
    .sort((left, right) => right.localeCompare(left))[0];
  const hasVolumes = props.entries.some((entry) => entry.matrix?.totalVolume !== null && entry.matrix?.totalVolume !== undefined);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [exportMessage, setExportMessage] = useState<string | undefined>(undefined);

  // The hedge position bar's values, matching HedgePosition.
  function exportBar(summary: HedgePlanSeasonSummaryModel, recommendationPercent: number, amount: (percent: number | null | undefined, volume: number | null | undefined) => string): HedgePlanExportBar {
    return {
      minimumPercent: summary.minimumHedgePercent,
      maximumPercent: summary.maximumHedgePercent,
      currentPercent: summary.hedgedPercent ?? 0,
      minimumLabel: amount(summary.minimumHedgePercent, summary.minimumHedgeVolume),
      maximumLabel: amount(summary.maximumHedgePercent, summary.maximumHedgeVolume),
      currentLabel: amount(summary.hedgedPercent, summary.hedgedVolume),
      recommended: recommendationPercent > 0,
    };
  }

  // The table as it is on screen, with the same columns and cell text, for PowerPoint and the clipboard.
  function buildExportTable(): HedgePlanExportTable {
    const headerFill = readHedgePlanColor('--stoneX-midnight');
    const header = (text: string, extra: Partial<HedgePlanExportCell> = {}): HedgePlanExportCell => ({ text, fill: headerFill, ...extra });
    const fillMarker = (status?: HedgePlanTriggerFulfillmentStatus) => (status === HedgePlanTriggerFulfillmentStatus.Full ? ' ●' : status === HedgePlanTriggerFulfillmentStatus.Partial ? ' ◐' : '');
    const triggerColumns = showTriggerColumns ? valuePercentiles.length + maximumBudgetTargets + maximumTimeTargets : 0;

    const headerRows: HedgePlanExportCell[][] = [
      [
        header('Period', { rowspan: 2 }),
        header(latestQuoteDate ? dayjs(latestQuoteDate.substring(0, 10)).format('M/D/YYYY') : 'Quote Date', { colspan: props.showBudgetPrice ? 3 : 2 }),
        ...(showTriggerColumns && valuePercentiles.length > 0 ? [header('Historical Value Triggers', { colspan: valuePercentiles.length })] : []),
        ...(showTriggerColumns && maximumBudgetTargets > 0 ? [header('Budget Triggers', { colspan: maximumBudgetTargets })] : []),
        ...(showTriggerColumns && maximumTimeTargets > 0 ? [header('Time Targets', { colspan: maximumTimeTargets })] : []),
        header('Hedge Position', { rowspan: 2 }),
        header('Hedge Recommendation', { rowspan: 2 }),
      ],
      [
        ...(props.showBudgetPrice ? [header('Budget Price')] : []),
        header('Average Hedge Price'),
        header('Current Market Price'),
        ...(showTriggerColumns ? valuePercentiles.map((percentile) => header(`${ordinal(percentile)} Decile`)) : []),
        ...(showTriggerColumns ? Array.from({ length: maximumBudgetTargets }, (_, index) => header(`${ordinal(index + 1)} Budget Trigger`)) : []),
        ...(showTriggerColumns ? Array.from({ length: maximumTimeTargets }, (_, index) => header(`${ordinal(index + 1)} Time Target`)) : []),
      ],
    ];

    const bodyRows: HedgePlanExportCell[][] = props.periods.map((period) => {
      const entry = props.entries.find((candidate) => candidate.periodKey === period.key);
      const summary = entry?.matrix?.summary;
      const amount = (percent: number | null | undefined, volume: number | null | undefined) => formatHedgeAmount(percent, volume, props.showVolumes);
      const progressFor = (triggerType: HedgePlanExecutionType, triggerId: number) =>
        summary?.triggers.find((progress) => progress.triggerType === triggerType && progress.triggerId === triggerId);
      const valueTriggersByPercentile = new Map((entry?.valueTriggers ?? []).filter((trigger) => !trigger.disabled).map((trigger) => [trigger.decilePercentile, trigger]));
      const budgetTriggers = (entry?.budgetTriggers ?? []).filter((trigger) => !trigger.disabled).sort((left, right) => left.price - right.price);
      const timeTriggers = (entry?.timeTriggers ?? []).filter((trigger) => !trigger.disabled).sort((left, right) => left.triggerDate.localeCompare(right.triggerDate));
      const recommendationPercent = summary?.recommendationPercent ?? 0;
      const reasons = summary ? summary.recommendationReasons.map((reason) => RECOMMENDATION_REASON_LABELS[reason]).join(', ') : '';

      return [
        { text: entry?.matrix?.volumeUnit ? `${period.label}\n${formatSeasonVolume(entry.matrix)}` : period.label, bold: true },
        ...(props.showBudgetPrice ? [{ text: formatPrice(summary?.budgetPrice) }] : []),
        { text: formatPrice(summary?.averageHedgePrice) },
        { text: formatPrice(summary?.currentMarketPrice) },
        ...(showTriggerColumns ? valuePercentiles.map((percentile) => {
          const trigger = valueTriggersByPercentile.get(percentile);
          const progress = trigger ? progressFor(HedgePlanExecutionType.Value, trigger.hedgePlanValueTriggerId) : undefined;
          return { text: trigger ? amount(trigger.targetHedgePercent, progress?.targetHedgeVolume) + fillMarker(progress?.status) : '' };
        }) : []),
        ...(showTriggerColumns ? Array.from({ length: maximumBudgetTargets }, (_, index) => {
          const trigger = budgetTriggers[index];
          const progress = trigger ? progressFor(HedgePlanExecutionType.Budget, trigger.hedgePlanBudgetTriggerId) : undefined;
          return { text: trigger ? `${formatPrice(trigger.price)}\n${amount(trigger.targetHedgePercent, progress?.targetHedgeVolume)}${fillMarker(progress?.status)}` : '' };
        }) : []),
        ...(showTriggerColumns ? Array.from({ length: maximumTimeTargets }, (_, index) => {
          const trigger = timeTriggers[index];
          const progress = trigger ? progressFor(HedgePlanExecutionType.Time, trigger.hedgePlanTimeTriggerId) : undefined;
          return { text: trigger ? `${dayjs(trigger.triggerDate.substring(0, 10)).format('M/D/YY')}\n${amount(trigger.targetHedgePercent, progress?.targetHedgeVolume)}${fillMarker(progress?.status)}` : '' };
        }) : []),
        {
          text: summary
            ? `Hedged ${amount(summary.hedgedPercent, summary.hedgedVolume)}\nMin ${amount(summary.minimumHedgePercent, summary.minimumHedgeVolume)} · Max ${amount(summary.maximumHedgePercent, summary.maximumHedgeVolume)}`
            : '-',
          bar: summary ? exportBar(summary, recommendationPercent, amount) : undefined,
        },
        {
          text: summary ? amount(summary.recommendationPercent, summary.recommendationVolume) + (reasons ? `\n${reasons}` : '') : '',
          fill: recommendationPercent !== 0 ? 'FDECEC' : undefined,
          color: recommendationPercent !== 0 ? 'C62828' : undefined,
          bold: true,
        },
      ];
    });

    return {
      title: `${props.exportTitle} Hedge Plan`,
      subtitle: latestQuoteDate ? `As of ${dayjs(latestQuoteDate.substring(0, 10)).format('M/D/YYYY')}` : '',
      headerRows,
      bodyRows,
      bodyRowKeys: props.periods.map((period) => period.key),
      columnWidths: [1.4, ...(props.showBudgetPrice ? [1] : []), 1, 1, ...Array.from({ length: triggerColumns }, () => 0.9), 2, 1.6],
    };
  }

  async function runExport(action: 'powerpoint' | 'clipboard') {
    setExportMenuAnchor(null);
    try {
      if (action === 'powerpoint') {
        await downloadHedgePlanPowerPoint(buildExportTable(), props.getChartSlides?.());
      } else {
        await copyHedgePlanTable(buildExportTable());
        setExportMessage('Table copied. Paste it into PowerPoint as an editable table.');
      }
    } catch {
      setExportMessage(action === 'powerpoint' ? 'The PowerPoint could not be created.' : 'The table could not be copied.');
    }
  }
  if (props.periods.length === 0) {
    return null;
  }

  const headerCellSx = {
    padding: '8px 10px !important',
    border: `1px solid ${BORDER_COLOR}`,
    color: 'var(--white)',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.25,
    textAlign: 'center',
    verticalAlign: 'middle',
  };
  const bodyCellSx = {
    boxSizing: 'border-box',
    padding: '8px 10px !important',
    border: `1px solid ${BORDER_COLOR}`,
    whiteSpace: 'nowrap',
    fontSize: '13px',
    height: BODY_ROW_HEIGHT,
    textAlign: 'center',
    verticalAlign: 'middle',
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Box sx={{ position: 'relative', width: 'fit-content', margin: '0 auto' }}>
        <Paper variant="outlined" sx={{ width: 'fit-content', borderRadius: '4px', borderColor: 'var(--box-border-color)', overflow: 'visible' }}>
        <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0, fontFamily: 'var(--font-family)' }}>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ ...headerCellSx, minWidth: PERIOD_COLUMN_MINIMUM_WIDTH, whiteSpace: 'nowrap', backgroundColor: HEADER_COLOR }}>Period</TableCell>
              <TableCell colSpan={props.showBudgetPrice ? 3 : 2} sx={{ ...headerCellSx, backgroundColor: HEADER_COLOR }}>
                {latestQuoteDate ? dayjs(latestQuoteDate.substring(0, 10)).format('M/D/YYYY') : 'Quote Date'}
              </TableCell>
              {showTriggerColumns && valuePercentiles.length > 0 && (
                <TableCell colSpan={valuePercentiles.length} sx={{ ...headerCellSx, backgroundColor: HEADER_COLOR }}>
                  Historical Value Triggers
                </TableCell>
              )}
              {showTriggerColumns && maximumBudgetTargets > 0 && (
                <TableCell colSpan={maximumBudgetTargets} sx={{ ...headerCellSx, backgroundColor: HEADER_COLOR }}>
                  Budget Triggers
                </TableCell>
              )}
              {showTriggerColumns && maximumTimeTargets > 0 && (
                <TableCell colSpan={maximumTimeTargets} sx={{ ...headerCellSx, backgroundColor: HEADER_COLOR }}>
                  Time Targets
                </TableCell>
              )}
              <TableCell rowSpan={2} sx={{ ...headerCellSx, ...fixedColumnWidth(HEDGE_POSITION_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>Hedge Position</TableCell>
              <TableCell rowSpan={2} sx={{ ...headerCellSx, ...fixedColumnWidth(RECOMMENDATION_COLUMN_WIDTH), position: 'relative', backgroundColor: HEADER_COLOR }}>
                Hedge Recommendation
                {!props.readOnly && (
                  <Box sx={{ position: 'absolute', top: 0, left: 'calc(100% + 8px)', height: '100%', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Tooltip title="Plan settings">
                      <IconButton aria-label="Plan settings" onClick={props.onOpenSettings}>
                        <Settings fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={exportMessage ?? 'Export the table'} onClose={() => setExportMessage(undefined)}>
                      <IconButton aria-label="Export the table" aria-haspopup="menu" onClick={(event) => setExportMenuAnchor(event.currentTarget)}>
                        <FileDownload fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={exportMenuAnchor} open={!!exportMenuAnchor} onClose={() => setExportMenuAnchor(null)}>
                      <MenuItem onClick={() => runExport('powerpoint')}>Download PowerPoint</MenuItem>
                      <MenuItem onClick={() => runExport('clipboard')}>Copy table</MenuItem>
                    </Menu>
                    <Tooltip title={showTriggerColumns ? 'Hide trigger columns' : 'Show trigger columns'}>
                      <IconButton
                        aria-label={showTriggerColumns ? 'Hide trigger columns' : 'Show trigger columns'}
                        aria-pressed={!showTriggerColumns}
                        onClick={() => setShowTriggerColumns((current) => !current)}
                      >
                        {showTriggerColumns ? <VisibilityOff fontSize="medium" /> : <Visibility fontSize="medium" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={!props.canWeightByVolume
                      ? 'Link a budget source in plan settings to weight the price curve by budgeted volume'
                      : props.weightByVolume ? 'Stop weighting the price curve by volume' : 'Weight the price curve by volume'}
                    >
                      <span>
                        <IconButton
                          aria-label={props.weightByVolume ? 'Stop weighting the price curve by volume' : 'Weight the price curve by volume'}
                          aria-pressed={props.weightByVolume}
                          color={props.weightByVolume ? 'primary' : 'default'}
                          disabled={!props.canWeightByVolume}
                          onClick={props.onToggleWeightByVolume}
                        >
                          <Balance fontSize="medium" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={!hasVolumes
                      ? 'Volumes show once a season with a complete budget has run'
                      : props.showVolumes ? 'Showing volumes. Click to show percentages' : 'Showing percentages. Click to show volumes'}
                    >
                      <span>
                        {/* A fixed size, so the click target doesn't change as the label switches between % and Vol. */}
                        <IconButton
                          aria-label={props.showVolumes ? 'Show hedges as percentages' : 'Show hedges as volumes'}
                          aria-pressed={props.showVolumes}
                          color={props.showVolumes ? 'primary' : 'default'}
                          disabled={!hasVolumes}
                          onClick={props.onToggleShowVolumes}
                          sx={{ width: 40, height: 40 }}
                        >
                          <Box component="span" sx={{ fontSize: '13px', fontWeight: 700 }}>
                            {props.showVolumes ? 'Vol' : '%'}
                          </Box>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              {props.showBudgetPrice && (
                <TableCell sx={{ ...headerCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>
                  <Tooltip title="The underlying budget price, weighted by budgeted volume, in the product's currency">
                    <span>Budget Price</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell sx={{ ...headerCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>Average Hedge Price</TableCell>
              <TableCell sx={{ ...headerCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>Current Market Price</TableCell>
              {showTriggerColumns && valuePercentiles.map((percentile) => (
                <TableCell key={percentile} sx={{ ...headerCellSx, ...fixedColumnWidth(VALUE_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>
                  {ordinal(percentile)} Decile
                </TableCell>
              ))}
              {showTriggerColumns && Array.from({ length: maximumBudgetTargets }, (_, index) => (
                <TableCell key={index} sx={{ ...headerCellSx, ...fixedColumnWidth(BUDGET_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>
                  {ordinal(index + 1)} Budget Trigger
                </TableCell>
              ))}
              {showTriggerColumns && Array.from({ length: maximumTimeTargets }, (_, index) => (
                <TableCell key={index} sx={{ ...headerCellSx, ...fixedColumnWidth(TIME_COLUMN_WIDTH), backgroundColor: HEADER_COLOR }}>
                  {ordinal(index + 1)} Time Target
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.periods.map((period, rowIndex) => {
              const entry = props.entries.find((candidate) => candidate.periodKey === period.key);
              const isLoading = props.dataLoading || props.loadingPeriodKeys.has(period.key);
              if (!entry) {
                const blankCellCount = 4
                  + (props.showBudgetPrice ? 1 : 0)
                  + (showTriggerColumns ? valuePercentiles.length : 0)
                  + (showTriggerColumns ? maximumBudgetTargets : 0)
                  + (showTriggerColumns ? maximumTimeTargets : 0);
                return (
                  <TableRow key={period.key} sx={{ backgroundColor: rowIndex % 2 === 0 ? 'var(--white)' : 'var(--gray9)' }}>
                    <TableCell component="th" scope="row" sx={{ ...bodyCellSx, minWidth: PERIOD_COLUMN_MINIMUM_WIDTH, fontWeight: 700, backgroundColor: 'var(--gray9)', color: 'var(--font-color)' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        {isLoading && <CircularProgress size={16} thickness={5} aria-label={`Loading ${period.label}`} />}
                        <span>{period.label}</span>
                      </Box>
                    </TableCell>
                    {Array.from({ length: blankCellCount }, (_, index) => {
                      const isLastCell = index === blankCellCount - 1;
                      return (
                        <TableCell key={index} sx={{ ...bodyCellSx, ...(isLastCell ? { position: 'relative' as const } : {}), color: 'var(--font-color)' }}>
                          {isLastCell && (
                            <PeriodActions
                              periodKey={period.key}
                              periodLabel={period.label}
                              chartSelected={period.key === props.selectedPeriodKey}
                              chartDisabled
                              onToggleChart={props.onToggleChart}
                              onManageSeason={props.readOnly ? undefined : props.onManageSeason}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              }

              const valueTriggersByPercentile = new Map<number, HedgePlanValueTriggerModel>(
                entry.valueTriggers.filter((trigger) => !trigger.disabled).map((trigger) => [trigger.decilePercentile, trigger]),
              );
              const budgetTriggers: HedgePlanBudgetTriggerModel[] = entry.budgetTriggers
                .filter((trigger) => !trigger.disabled)
                .sort((left, right) => left.price - right.price);
              const timeTriggers = entry.timeTriggers
                .filter((trigger) => !trigger.disabled)
                .sort((left, right) => left.triggerDate.localeCompare(right.triggerDate));
              const isMatrixLoading = props.loadingPeriodKeys.has(period.key);
              const summary = entry.matrix?.summary;
              const amount = (percent: number | null | undefined, volume: number | null | undefined) => formatHedgeAmount(percent, volume, props.showVolumes);
              const progressFor = (triggerType: HedgePlanExecutionType, triggerId: number) =>
                summary?.triggers.find((progress) => progress.triggerType === triggerType && progress.triggerId === triggerId);
              const recommendationPercent = summary?.recommendationPercent ?? 0;

              return (
                <TableRow
                  key={entry.periodKey}
                  hover
                  sx={{
                    backgroundColor: rowIndex % 2 === 0 ? 'var(--white)' : 'var(--gray9)',
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ ...bodyCellSx, minWidth: PERIOD_COLUMN_MINIMUM_WIDTH, fontWeight: 700, textAlign: 'center', backgroundColor: 'var(--gray9)', color: 'var(--font-color)' }}>
                    {entry.periodLabel}
                    {entry.matrix?.volumeUnit && (
                      <Box sx={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--gray4)' }}>
                        {formatSeasonVolume(entry.matrix)}
                      </Box>
                    )}
                  </TableCell>
                  {props.showBudgetPrice && (
                    <TableCell sx={{ ...bodyCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), color: 'var(--font-color)' }}>
                      {isMatrixLoading && !summary
                        ? <CircularProgress size={14} thickness={5} aria-label="Loading price" />
                        : formatPrice(summary?.budgetPrice)}
                    </TableCell>
                  )}
                  <TableCell sx={{ ...bodyCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), color: 'var(--font-color)' }}>
                    {isMatrixLoading && !summary
                      ? <CircularProgress size={14} thickness={5} aria-label="Loading price" />
                      : formatPrice(summary?.averageHedgePrice)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, ...fixedColumnWidth(PRICE_COLUMN_WIDTH), color: 'var(--font-color)' }}>
                    {isMatrixLoading && !summary
                      ? <CircularProgress size={14} thickness={5} aria-label="Loading price" />
                      : formatPrice(summary?.currentMarketPrice)}
                  </TableCell>
                  {showTriggerColumns && valuePercentiles.map((percentile) => {
                    const trigger = valueTriggersByPercentile.get(percentile);
                    const progress = trigger ? progressFor(HedgePlanExecutionType.Value, trigger.hedgePlanValueTriggerId) : undefined;
                    return trigger ? (
                      <TargetCell key={percentile} section="value" status={progress?.status}>
                        {amount(trigger.targetHedgePercent, progress?.targetHedgeVolume)}
                      </TargetCell>
                    ) : <TableCell key={percentile} sx={{ ...bodyCellSx, ...fixedColumnWidth(VALUE_COLUMN_WIDTH), backgroundColor: VALUE_CELL_COLOR }} />;
                  })}
                  {showTriggerColumns && Array.from({ length: maximumBudgetTargets }, (_, index) => {
                    const trigger: HedgePlanBudgetTriggerModel | undefined = budgetTriggers[index];
                    const progress = trigger ? progressFor(HedgePlanExecutionType.Budget, trigger.hedgePlanBudgetTriggerId) : undefined;
                    return trigger ? (
                      <TargetCell key={index} section="budget" status={progress?.status}>
                        <Box>{formatPrice(trigger.price)}</Box>
                        <Box>{amount(trigger.targetHedgePercent, progress?.targetHedgeVolume)}</Box>
                      </TargetCell>
                    ) : <TableCell key={index} sx={{ ...bodyCellSx, ...fixedColumnWidth(BUDGET_COLUMN_WIDTH), backgroundColor: BUDGET_CELL_COLOR }} />;
                  })}
                  {showTriggerColumns && Array.from({ length: maximumTimeTargets }, (_, index) => {
                    const trigger: HedgePlanTimeTriggerModel | undefined = timeTriggers[index];
                    const progress = trigger ? progressFor(HedgePlanExecutionType.Time, trigger.hedgePlanTimeTriggerId) : undefined;
                    return trigger ? (
                      <TargetCell key={index} section="time" status={progress?.status}>
                        <Box>{dayjs(trigger.triggerDate.substring(0, 10)).format('M/D/YY')}</Box>
                        <Box>{amount(trigger.targetHedgePercent, progress?.targetHedgeVolume)}</Box>
                      </TargetCell>
                    ) : <TableCell key={index} sx={{ ...bodyCellSx, ...fixedColumnWidth(TIME_COLUMN_WIDTH), backgroundColor: TIME_CELL_COLOR }} />;
                  })}
                  <TableCell align="center" sx={{ ...bodyCellSx, ...fixedColumnWidth(HEDGE_POSITION_COLUMN_WIDTH), padding: '0 !important', backgroundColor: 'var(--gray8)', color: 'var(--font-color)' }}>
                    {summary ? (
                      <HedgePosition
                        minimumPercent={summary.minimumHedgePercent}
                        maximumPercent={summary.maximumHedgePercent}
                        currentPercent={summary.hedgedPercent ?? 0}
                        recommendationPercent={recommendationPercent}
                        minimumLabel={amount(summary.minimumHedgePercent, summary.minimumHedgeVolume)}
                        maximumLabel={amount(summary.maximumHedgePercent, summary.maximumHedgeVolume)}
                        currentLabel={amount(summary.hedgedPercent, summary.hedgedVolume)}
                      />
                    ) : isMatrixLoading
                      ? <CircularProgress size={16} thickness={5} aria-label="Loading hedge position" />
                      : '-'}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      ...bodyCellSx,
                      ...fixedColumnWidth(RECOMMENDATION_COLUMN_WIDTH),
                      position: 'relative',
                      backgroundColor: recommendationPercent !== 0 ? 'var(--red-cell-background)' : 'var(--gray8)',
                      color: recommendationPercent !== 0 ? 'var(--red-number)' : 'var(--font-color)',
                      fontWeight: 700,
                    }}
                  >
                    {summary ? amount(summary.recommendationPercent, summary.recommendationVolume) : ''}
                    {summary && summary.recommendationReasons.length > 0 && (
                      <Tooltip title={summary.recommendationReasons.map((reason) => RECOMMENDATION_REASON_LABELS[reason]).join(', ')}>
                        <Box
                          component="span"
                          sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            display: 'inline-flex',
                            color: 'var(--red-number)',
                          }}
                        >
                          <PriorityHigh sx={{ fontSize: 14 }} />
                        </Box>
                      </Tooltip>
                    )}
                    <PeriodActions
                      periodKey={entry.periodKey}
                      periodLabel={entry.periodLabel}
                      chartSelected={entry.periodKey === props.selectedPeriodKey}
                      chartDisabled={false}
                      onToggleChart={props.onToggleChart}
                      onManageSeason={props.readOnly ? undefined : props.onManageSeason}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Paper>
        {!props.readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 1 }}>
            <StoneXButton variant="outlined" startIcon={<Add />} onClick={props.onAddNextSeason} isWorking={props.addingNextSeason}>
              Add {props.nextSeasonLabel}
            </StoneXButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
